// Where the tenant key becomes BYTES for an operation that needs them
// (ADR 0009, "Two operations cannot run on a handle, ever").
//
// Everything else runs on a non-extractable `CryptoKey`, which an XSS can
// use while the page is open but cannot steal. A handful of operations
// cannot: they take the private key BYTES as the plaintext they seal or
// emit. There is no handle-shaped version of "encrypt this key for
// someone else", nor of "write this key into a file" — the bytes have to
// exist.
//
// BE PRECISE ABOUT HOW MANY PLACES THAT IS, because an earlier version of
// this comment claimed "one" and was wrong. There are exactly TWO, both
// in this directory and both zeroing in a `finally`:
//
//   - `unlock()` in `vault-unlock.ts`, which parses a pasted recovery
//     file and imports it straight into a non-extractable handle. It is
//     the paste path; the bytes exist only between the parse and the
//     import.
//   - `runRawKeyCeremony` below, for the operations whose payload the
//     bytes ARE.
//
// Two is the whole list, and since the security review that found the
// previous claim to be an honour system it is ENFORCED rather than
// asserted: `eslint.config.mjs` carries a `no-restricted-imports` rule
// making `privateKeyFromRecoveryFile` importable by those two modules and
// no others, and this module importable only through `vault-unlock.ts`.
// A third route to the bytes now fails lint rather than passing review.
//
// WHERE THE BYTES COME FROM, AND WHY IT IS A PASTE. Before ADR 0009 the
// stored key was a PRF-wrapped blob, so the ceremony could re-open it
// with a second authenticator gesture. ADR 0009 removed the passkey: what
// IndexedDB now holds is the non-extractable handle itself, which is
// precisely a thing that cannot produce bytes. With PRF gone there is no
// second factor left to re-authenticate with, so the honest design is to
// ask the operator for the recovery file again, for that one operation.
//
// That is friction, and it is deliberate friction rather than leftover
// friction. The alternative on offer was "no confirmation at all", which
// would mean any script that can call `sealKekToDaemon` can ship the
// tenant key to a host of its choosing with nothing on screen. Asking for
// a file the operator keeps offline is the only remaining thing an
// attacker in the page does not have.
//
// SHAPE. A ceremony is interactive, so this module cannot just call a
// browser API and await it. It publishes a pending request as
// subscribable state; `VaultCeremonyPrompt` renders it, and `submit` /
// `cancel` below are what that component calls. One at a time, by
// construction: two prompts asking for the same file, each claiming a
// different reason, is exactly the confusion a confirmation exists to
// prevent.
import { privateKeyFromRecoveryFile } from './vault-recovery-file.ts'

/** What `vault-key-session.ts` knows about the live session that this
 * module needs. Passed in rather than imported to keep the dependency
 * one-way: the session owns the ceremony, not the other way round. */
export interface RawKeyHolder {
  /** Base64url tenant public key. The pasted file is verified against
   * THIS, not against whatever the server last said, so the bytes about
   * to be used are provably the bytes this session is using. */
  tenantPubkey: string
}

/** The operator dismissed the prompt. Its own class because every caller
 * has to tell "you decided not to" from "it went wrong": the first is a
 * silent no-op, the second is a red callout. */
export class CeremonyCancelledError extends Error {
  constructor() {
    super('the recovery-file confirmation was dismissed, so nothing ran')
    this.name = 'CeremonyCancelledError'
  }
}

/** A second ceremony was requested while one was already on screen.
 *
 * Refused rather than queued. A queue would show the operator a second
 * identical-looking prompt after they answered the first, with no way to
 * tell which operation the file they are about to paste is for — and a
 * confirmation whose subject is ambiguous is not a confirmation. */
export class CeremonyBusyError extends Error {
  constructor(reason: string) {
    super(
      `another operation is already asking for the recovery file ` +
        `(${reason}) — finish or dismiss that one first`,
    )
    this.name = 'CeremonyBusyError'
  }
}

/** What the prompt component renders. Deliberately carries no key
 * material and no promise handles: it is the view of the ceremony, and
 * everything in it is safe to put on a screen. */
export interface CeremonyRequest {
  /** The operation asking, named well enough that an operator can check
   * it against what they just clicked. "Deliver the tenant key to the
   * daemon node01-5a28b0", not "a daemon". */
  reason: string
  /** The previous paste's failure, or `null`. Kept on the request rather
   * than aborting the whole operation, because a mistyped file is a
   * retry and not a reason to make the operator start the approval
   * again. */
  error: string | null
}

interface PendingCeremony {
  tenantPubkey: string
  /** SECURITY: resolved with key material. `runRawKeyCeremony` is the
   * only waiter and owns zeroing it. */
  resolve: (pkcs8: Uint8Array) => void
  reject: (err: Error) => void
}

let pending: PendingCeremony | null = null

/** The view of `pending`, kept beside it rather than derived on read.
 *
 * Two reasons, and both are load-bearing. `useSyncExternalStore` compares
 * snapshots by identity and re-renders forever if `getCeremony()` builds
 * a fresh object each call, so the snapshot has to be a stored value that
 * changes identity exactly when something changed — which is why a failed
 * paste REPLACES this object instead of assigning to `.error`. And
 * `pending` carries `resolve`/`reject`, which have no business being
 * handed to a component: the only ways to finish a ceremony are `submit`
 * and `cancel` below. */
let snapshot: CeremonyRequest | null = null

const listeners = new Set<() => void>()

function setCeremony(
  next: PendingCeremony | null,
  view: CeremonyRequest | null,
): void {
  pending = next
  snapshot = view
  for (const listener of listeners) listener()
}

/** Subscribes to a ceremony starting, failing a paste, and finishing.
 * Shaped for `useSyncExternalStore`; see `useRawKeyCeremony` in
 * `vault-unlock.ts`. */
export function subscribeCeremony(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The ceremony currently on screen, or `null`. */
export function getCeremony(): CeremonyRequest | null {
  return snapshot
}

/**
 * Answers the prompt with a pasted recovery file.
 *
 * A file that does not parse, or whose public half is not this session's,
 * leaves the prompt up with the reason attached. Only a valid file
 * closes it. The text is never stored here — it is parsed on the spot and
 * the caller is expected to drop its own copy immediately, because a
 * recovery file held in React state is a tenant private key held in
 * React state.
 */
export function submitCeremony(recoveryFileText: string): void {
  const current = pending
  const view = snapshot
  if (current === null || view === null) return
  let pkcs8: Uint8Array
  try {
    pkcs8 = privateKeyFromRecoveryFile(recoveryFileText, current.tenantPubkey)
  } catch (err) {
    // Reported to the operator, never swallowed. `vault-recovery-file.ts`
    // writes these messages for exactly this surface and puts no key
    // material in them.
    setCeremony(current, {
      reason: view.reason,
      error: err instanceof Error ? err.message : String(err),
    })
    return
  }
  // Cleared before resolving, not after: the paste is done by now, and
  // leaving the prompt up for the duration of the work would make a slow
  // seal look like a prompt the operator failed to answer.
  setCeremony(null, null)
  current.resolve(pkcs8)
}

/** Dismisses the prompt. The waiting operation rejects with
 * `CeremonyCancelledError` and nothing it was going to do happens. */
export function cancelCeremony(): void {
  const current = pending
  if (current === null) return
  setCeremony(null, null)
  current.reject(new CeremonyCancelledError())
}

/**
 * Runs `fn` with the tenant private key as PKCS8 bytes, then zeroes them.
 *
 * Puts the prompt on screen, waits for a valid recovery file, and only
 * then calls `fn`. The `finally` is the whole contract: `fn` throwing,
 * `fn` returning, and `fn` rejecting all land on the same wipe. Nothing
 * here returns the array, and `fn` must not keep a reference past its own
 * return — after that the buffer it holds is all zeroes, which is a
 * silent wrong answer rather than an error.
 *
 * Rejects with `CeremonyCancelledError` if the operator dismisses the
 * prompt, and with `CeremonyBusyError` if one is already up.
 */
export async function runRawKeyCeremony<T>(
  holder: RawKeyHolder,
  reason: string,
  fn: (pkcs8: Uint8Array) => Promise<T> | T,
): Promise<T> {
  if (snapshot !== null) throw new CeremonyBusyError(snapshot.reason)
  const pkcs8 = await new Promise<Uint8Array>((resolve, reject) => {
    setCeremony(
      { tenantPubkey: holder.tenantPubkey, resolve, reject },
      { reason, error: null },
    )
  })
  try {
    return await fn(pkcs8)
  } finally {
    pkcs8.fill(0)
  }
}
